import styles from "./CreateContract.module.scss";
import React, { useEffect, useRef, useState } from "react";
import { Form, FormInstance } from "antd";
import {
  ContractLanguageEnum,
  IContractForTable,
  ICreateOrUpdateContract,
} from "../../../models/Contract";
import { openErrorMessage, openSuccessMessage } from "../../../store/message/messageSlice";
import { handleError } from "../../../helper/errorHandler";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { useContractMutations } from "../../../api/mutations";
import FormItemSelect from "../../../components/ui/FormItemSelect/FormItemSelect";
import RichTextEditor from "../../../components/ui/RichTextEditor/RichTextEditor";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import closeIcon from "../../../assets/images/icons/close-grey.svg";
import { convertQuillToInlineStyles } from "../../../helper/convertQuillToInlineStyles";

interface ICreateContractProps {
  selectedContract: IContractForTable | null;
  refetch: () => void;
  languageSelectItems: { id: number; label: string }[];
  setSelectedContract: (value: IContractForTable | null) => void;
  closePopup: () => void;
}

const CreateContract: React.FC<ICreateContractProps> = ({
  selectedContract,
  refetch,
  languageSelectItems,
  setSelectedContract,
  closePopup,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<FormInstance>(null);
  const { createOrUpdateContract } = useContractMutations();

  const defaultLanguage = languageSelectItems[0].id;

  const [content, setContent] = useState("");

  useEffect(() => {
    if (!selectedContract || !formRef.current) return;
    formRef.current.setFieldsValue({
      title: selectedContract.title,
      language: Number(selectedContract.language),
    });
    setContent(convertQuillToInlineStyles(selectedContract.content));
  }, [selectedContract]);

  const onFinish = (values: { title: string; language: ContractLanguageEnum }) => {
    if (!content) {
      dispatch(openErrorMessage(t("ContentCanNotBeEmpty")));
      return;
    }
    const model: ICreateOrUpdateContract = {
      title: values.title,
      language: values.language,
      content: convertQuillToInlineStyles(content),
    };
    if (selectedContract) {
      model.contractVersionId = selectedContract.contractVersionId;
    }
    createOrUpdateContract(model, {
      onSuccess: () => {
        dispatch(openSuccessMessage(t("ContractCreateSuccessfully")));
        reset();
        refetch();
        closePopup();
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const reset = () => {
    formRef.current?.setFieldsValue({
      title: "",
      language: defaultLanguage,
    });
    setContent("");
    setSelectedContract(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        <button onClick={closePopup} className={styles.closeBtn}>
          <img src={closeIcon} alt="close" />
        </button>
        <div className={`${styles.popupContent} custom-scroll`}>
          <div className={"top-row"}>
            <Form
              className={"form"}
              ref={formRef}
              name="create-contract"
              onFinish={onFinish}
              autoComplete="off"
              layout={"vertical"}
              initialValues={{
                title: "",
                language: defaultLanguage,
              }}
            >
              <FormItemInput
                disabled={!!selectedContract}
                name={"title"}
                label={t("ContractTitle")}
                rules={{ required: true }}
                style={{
                  width: "300px",
                }}
              />

              <FormItemSelect
                name={"language"}
                label={t("SelectLanguage")}
                rules={{ required: true }}
                items={languageSelectItems}
                style={{
                  width: "300px",
                }}
              />
            </Form>
          </div>

          {selectedContract && selectedContract.version && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ marginBottom: "10px" }}>Version: {selectedContract.version}</div>
              <div>Language: {ContractLanguageEnum[Number(selectedContract.language)]}</div>
            </div>
          )}

          <RichTextEditor
            value={content}
            setValue={setContent}
            style={{ width: "100%", height: "450px" }}
          />

          <div style={{ marginTop: "65px", display: "flex", gap: "20px" }}>
            <CustomButton
              text={t("SaveChanges")}
              type={"button"}
              typeStyle={"fill"}
              style={{ width: "400px" }}
              functionOnClick={() => formRef.current?.submit()}
            />
            <CustomButton
              text={t("Reset")}
              type={"button"}
              typeStyle={"outline"}
              style={{ width: "400px" }}
              functionOnClick={() => reset()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateContract;
